// GLB models from art/ — the loader, the part merger, and the per-team paint.
//
// The game's units used to be merged boxes: one vertex-colored geometry per
// silhouette, one shared Lambert material, one draw call. The Blender-built
// models in art/ (contract: art/model-integration.md) are richer — a named
// node per part whose origin IS its pivot, a dozen flat-colored PBR materials
// per model, a single `*_Team` material for the team hue. This module turns
// the one into the other without losing either:
//
//   * Every named node keeps its name, parent and local transform, so a
//     propeller still spins on its own X, a turret still yaws on its own Y and
//     `node("Wing_L").visible = false` still shears a wing.
//   * Inside a node, every flat-colored primitive is merged into ONE geometry
//     whose vertex colors carry the materials. A Spitfire's thirty-odd
//     primitives become ten meshes; nothing about the look or the cost model of
//     the box era changes (vertex-colored Lambert, shared material).
//   * Materials that can't be a vertex color stay their own meshes: glass
//     (alpha-blended, depthWrite off, drawn after the hull), textured track
//     treads, the damage kits' alpha overlays.
//   * Paint is a per-(team, scheme) color attribute over SHARED position /
//     normal / index buffers — a Crimson and a Cobalt Sherman cost one set of
//     vertices on the GPU. `*_Team` takes TEAM_INFO[team].color; a wreck's
//     `*_Team_Char` keeps the faint team tint the art bakes into its char; a
//     paint scheme recolors the camouflage by material name (`*_Upper`,
//     `*_Side`… take the scheme body, `*_Lower`, `*_Belly`… its accent), and the
//     team hue is never touched, so friend and foe read at every range.
//   * A family can carry liveries: decal nodes (`BF_Livery_Galland`, a child
//     of the root holding its mottle and markings) that only the scheme naming
//     them shows. The catalog names the one the factory finish wears
//     (setModelOptions `livery`); a plain scheme repaints the skin and shows none.
//     The decals ride LOD0 alone. Where they are a camouflage covering half the
//     uppers (the Spitfire's Dark Green), the levels past it are told its color
//     (setModelOptions `camo`) and paint those surfaces as the mix of the two,
//     so the pattern blurs out at 250 m instead of switching off.
//   * A catalog can give a model family a `trim` (setModelOptions), and then it
//     flies no team at all: `*_Team` wears the trim — the airframe's own
//     spinner, wingtip and rudder paint — and a wreck chars from it. The
//     aircraft do: both air teams fly the same airframes in the same factory
//     finish, and the pilot's nameplate carries the side (planes.js), so a
//     Crimson and a Cobalt Spitfire share one painted variant outright.
//
// Loading is lazy and never blocks a frame: every visual builds its box
// silhouette exactly as before and swaps the model in when the promise lands.
// A slow connection, a portal build that can't reach the files, or a headless
// probe all still get a playable game on frame one.

import * as THREE from "three";
import { GFX } from "./gfx.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "meshoptimizer/decoder";
import { TEAM_INFO } from "./teams.js";

// The build step (client/scripts/build-models.mjs) parks each part's meshes on
// holder children named with this prefix so quantization can't move a pivot.
// They fold back into their named parent here.
export const HOLDER = "__m";

// --- fetching ---------------------------------------------------------------
// Both URL helpers must stay literal `new URL(\`../models/${…}\`, import.meta.url)`
// forms: Vite rewrites them into a content-hashed asset map (assets/models/…),
// and in node (the unit tests) they resolve to plain file URLs.
export function modelUrl(stem) {
  return new URL(`../models/${stem}.glb`, import.meta.url).href;
}
export function damageKitUrl(stem) {
  return new URL(`../models/${stem}_damage.json`, import.meta.url).href;
}

let fetchGlb = async (stem) => {
  const res = await fetch(modelUrl(stem));
  if (!res.ok) throw new Error(`model ${stem}: HTTP ${res.status}`);
  return res.arrayBuffer();
};
let fetchJson = async (stem) => {
  const res = await fetch(damageKitUrl(stem));
  if (!res.ok) throw new Error(`damage kit ${stem}: HTTP ${res.status}`);
  return res.json();
};

/** Swap the byte source (node tests read client/models off disk). */
export function setModelSource({ glb, json } = {}) {
  if (glb) fetchGlb = glb;
  if (json) fetchJson = json;
}

// Models are an upgrade, never a dependency: `?boxes` (or setModelsEnabled
// false) keeps every unit on its box silhouette — the escape hatch for a
// device that chokes, and the A/B switch for screenshots and probes.
// Off the page (node: the unit tests, the headless tools) nothing fetches on
// its own — a test that wants a model loads it explicitly.
let enabled = typeof document !== "undefined";
try {
  if (/[?&]boxes\b/.test(globalThis.location?.search ?? "")) enabled = false;
} catch {
  /* no location */
}
export function modelsEnabled() {
  return enabled;
}
export function setModelsEnabled(on) {
  enabled = !!on;
}

const loader = new GLTFLoader();
loader.setMeshoptDecoder(MeshoptDecoder);

// --- shared materials ---------------------------------------------------------
// Two paint materials for every model in the game (single- and double-sided
// source materials), exactly like the box era's shared planeMaterial.
export const paintMaterial = new THREE.MeshLambertMaterial({ vertexColors: true, name: "model-paint" });
export const paintMaterialDS = new THREE.MeshLambertMaterial({
  vertexColors: true,
  side: THREE.DoubleSide,
  name: "model-paint-ds",
});
// The same paint for InstancedMesh draws (tree plantings, town blocks, the
// ordnance in the air). three.js keys a shader program on instancing, and it
// sorts the draw list by material, so one material drawn both ways leaves the
// forests and the airframes interleaved by depth, and every flip between them
// re-resolves the program — 6–10 % of the air frame before it had its own copy
// (docs/wasm-investigation.md). Two materials, two programs, no flipping.
export const paintMaterialDSInstanced = paintMaterialDS.clone();
paintMaterialDSInstanced.name = "model-paint-ds-instanced";
// A livery's decals (the Bf 109's mottle, chevrons and tallies: art/liverylib)
// float 1.2 cm off the skin in the same paint; pulled toward the camera so they
// win the depth test out to LOD0's 250 m instead of shimmering through it.
export const paintMaterialDecal = paintMaterial.clone();
paintMaterialDecal.name = "model-paint-decal";
paintMaterialDecal.polygonOffset = true;
paintMaterialDecal.polygonOffsetFactor = -1;
paintMaterialDecal.polygonOffsetUnits = -2;

// A converted (non-paint) material, cached per source material so every
// instance of a template shares it. Lambert keeps today's light rig and cost;
// the PBR metalness in the art would render black without an environment map.
function lambertOf(m) {
  const out = new THREE.MeshLambertMaterial({
    name: m.name,
    color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
    map: m.map ?? null,
    transparent: !!m.transparent,
    opacity: m.opacity ?? 1,
    side: m.side ?? THREE.FrontSide,
    alphaTest: m.alphaTest ?? 0,
  });
  if (m.emissive && (m.emissive.r || m.emissive.g || m.emissive.b)) {
    out.emissive.copy(m.emissive);
    out.emissiveIntensity = m.emissiveIntensity ?? 1;
    if (m.emissiveMap) out.emissiveMap = m.emissiveMap;
  }
  if (out.transparent) {
    // The contract: glass (and every alpha overlay) never writes depth, so the
    // hull behind a canopy isn't punched out by the canopy's own triangles.
    out.depthWrite = false;
    // Overlays sit 1–1.5 cm off the skin; pull them toward the camera so they
    // win the depth test at range instead of shimmering through it.
    out.polygonOffset = true;
    out.polygonOffsetFactor = -1;
    out.polygonOffsetUnits = -1;
  }
  return out;
}

// A material becomes vertex color unless something about it can't be baked.
function isPaint(m) {
  if (!m || m.transparent || m.map || m.alphaTest > 0) return false;
  if (m.emissive && (m.emissive.r > 0.001 || m.emissive.g > 0.001 || m.emissive.b > 0.001)) return false;
  return true;
}

// --- paint ----------------------------------------------------------------------
// Scheme recolor groups, by the material suffix after the model prefix. The
// camouflage uppers wear the scheme body, the undersides its accent; `…Ctl`
// (the control-surface shade of each) follows at the same shade ratio the art
// used. Team, glass, metal fittings and everything else are left alone.
//
// A marking the art paints on a camouflage surface is named for both: the Bf
// 109's `BF_Side_Rudder` is a rudder a livery can turn yellow on its own, and
// to every other scheme it is just more `Side`. A livery names its colors per
// material (`paint`: { Upper, Side_Rudder, Spinner… }): the exact suffix wins,
// then the surface it sits on, then the plain body/accent recolor.
const SCHEME_BODY = new Set(["Upper", "Side", "Metal", "SeaBlue", "Skin", "IntBlue", "Cream"]);
const SCHEME_ACCENT = new Set(["Lower", "Belly", "White"]);
const CHAR = new THREE.Color(0x22201d);

function paletteEntry(m) {
  const name = m.name || "";
  const suffix = name.includes("_") ? name.slice(name.indexOf("_") + 1) : name;
  const ctl = suffix.endsWith("Ctl");
  const base = ctl ? suffix.slice(0, -3) : suffix;
  // The surface a marking sits on (`Side_Rudder` → `Side`); a wreck's char
  // (`Upper_Char`) is never repainted, so it keeps no group.
  const group = /_Char$/.test(base) ? "" : base.split("_")[0];
  return {
    name,
    color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
    team: /Team$/.test(name),
    teamChar: /Team_Char$/.test(name),
    key: suffix,
    base,
    group,
    body: SCHEME_BODY.has(group),
    accent: SCHEME_ACCENT.has(group),
    ctl,
    // The art's own shade ratio between a surface and its control surface,
    // filled in once every palette entry is known (see finishPalette).
    ctlK: 1,
  };
}

function finishPalette(palette) {
  const byName = new Map(palette.map((p) => [p.name, p]));
  for (const p of palette) {
    if (!p.ctl) continue;
    const parent = byName.get(p.name.slice(0, -3));
    if (!parent) continue;
    const lum = (c) => c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
    const a = lum(parent.color);
    p.ctlK = a > 1e-4 ? Math.min(1.5, lum(p.color) / a) : 1;
  }
}

const _c = new THREE.Color();
// `trim` (a hex, or null) is a teamless family's own paint for its `*_Team`
// material; with it, `team` never reaches the colors at all.
function paintColor(entry, team, scheme, out, trim = null) {
  const hue = trim ?? TEAM_INFO[team]?.color ?? TEAM_INFO[0].color;
  if (entry.team) return out.set(hue);
  if (entry.teamChar) return out.set(hue).lerp(CHAR, 0.9);
  const lp = scheme?.paint;
  if (lp) {
    // A control surface the livery names outright wears it as given; one it
    // only names the parent of follows at the art's shade ratio.
    const own = lp[entry.key];
    if (own) return out.set(own);
    const hex = lp[entry.base] ?? lp[entry.group];
    if (hex) {
      out.set(hex);
      if (entry.ctl) out.multiplyScalar(entry.ctlK);
      return out;
    }
  }
  if (scheme) {
    const hex = entry.body ? scheme.body : entry.accent ? scheme.accent : null;
    if (hex) {
      out.set(hex);
      if (entry.ctl) out.multiplyScalar(entry.ctlK);
      return out;
    }
  }
  return out.copy(entry.color);
}

// --- the template: one parsed, merged GLB --------------------------------------

/**
 * A parsed model: `root` is a clean scene graph (named nodes only, merged
 * meshes inside), `nodes` indexes it by full node name, `palette` lists the
 * vertex-color materials, `prefix` is the model's node prefix (`SP_`), and
 * `trim` is a teamless family's `*_Team` paint (null: it wears the team hue).
 */
class Template {
  constructor(stem, root, palette, prefix, trim = null) {
    this.stem = stem;
    this.root = root;
    this.palette = palette;
    this.prefix = prefix;
    this.trim = trim;
    this.nodes = new Map();
    root.traverse((o) => {
      if (o.userData.part) this.nodes.set(o.name, o);
    });
  }

  /** A node by full name (`SP_Wing_L`) or by part name (`Wing_L`). */
  node(name) {
    return this.nodes.get(name) ?? this.nodes.get(this.prefix + name) ?? null;
  }

  /** Model-space bounding box of a node and everything under it. */
  bounds(name) {
    const n = this.node(name);
    if (!n) return null;
    this.root.updateMatrixWorld(true);
    return new THREE.Box3().setFromObject(n);
  }
}

// Merge a node's own primitives (everything under it that isn't another named
// node) into one paint mesh per material side plus one mesh per special
// material. `inv` is the inverse of the named node's world matrix so the merged
// vertices land in the node's local frame.
const _m4 = new THREE.Matrix4();
const _n3 = new THREE.Matrix3();
const _v = new THREE.Vector3();

function mergeParts(parts, withUv) {
  let verts = 0, idx = 0;
  for (const p of parts) {
    verts += p.geo.attributes.position.count;
    idx += p.geo.index ? p.geo.index.count : p.geo.attributes.position.count;
  }
  const pos = new Float32Array(verts * 3);
  const nor = new Float32Array(verts * 3);
  const uv = withUv ? new Float32Array(verts * 2) : null;
  const index = verts > 65535 ? new Uint32Array(idx) : new Uint16Array(idx);
  const ranges = [];
  let vo = 0, io = 0;
  for (const p of parts) {
    const g = p.geo;
    const P = g.attributes.position, N = g.attributes.normal, U = g.attributes.uv;
    _n3.getNormalMatrix(p.mat);
    for (let i = 0; i < P.count; i++) {
      _v.fromBufferAttribute(P, i).applyMatrix4(p.mat);
      pos[(vo + i) * 3] = _v.x;
      pos[(vo + i) * 3 + 1] = _v.y;
      pos[(vo + i) * 3 + 2] = _v.z;
      if (N) {
        _v.fromBufferAttribute(N, i).applyMatrix3(_n3).normalize();
        nor[(vo + i) * 3] = _v.x;
        nor[(vo + i) * 3 + 1] = _v.y;
        nor[(vo + i) * 3 + 2] = _v.z;
      }
      if (uv && U) {
        uv[(vo + i) * 2] = U.getX(i);
        uv[(vo + i) * 2 + 1] = U.getY(i);
      }
    }
    if (g.index) {
      for (let i = 0; i < g.index.count; i++) index[io + i] = g.index.getX(i) + vo;
      io += g.index.count;
    } else {
      for (let i = 0; i < P.count; i++) index[io + i] = vo + i;
      io += P.count;
    }
    ranges.push({ start: vo, count: P.count, pal: p.pal });
    vo += P.count;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  if (uv) geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return { geo, ranges };
}

// Base geometry → { ranges, palette, trim, variants: Map(key → painted geometry) }.
const paintInfo = new WeakMap();

// ModelInstance.flatten's merge: `meshes` (parts of an instance under `root`)
// grouped by material, each group baked into `root`'s frame as one geometry.
// A paint group merges the parts' unpainted bases and becomes a paint base
// itself (their palette ranges, end to end), so painted() colours it for any
// team and scheme. A special whose geometry carries more than mergeParts
// keeps (vertex colours, a second UV) is left out, and so is a material
// only one part wears (merging it saves nothing). Returns { batches: [{ geo,
// material, paint }], taken: indices into `meshes` the batches stand in for },
// or null when the merge wouldn't save a draw.
function flatGeometry(root, meshes) {
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const groups = new Map(); // material → parts
  meshes.forEach((o, k) => {
    const paint = !!o.userData.paint;
    const geo = paint ? (o.geometry.userData.paintBase ?? o.geometry) : o.geometry;
    if (paint && !paintInfo.has(geo)) return;
    if (!paint && Object.keys(geo.attributes).some((a) => a !== "position" && a !== "normal" && a !== "uv")) return;
    if (!groups.has(o.material)) groups.set(o.material, []);
    groups.get(o.material).push({ geo, mat: new THREE.Matrix4().multiplyMatrices(inv, o.matrixWorld), pal: -1, paint, k });
  });
  for (const [m, parts] of groups) if (parts.length < 2) groups.delete(m);
  if (!groups.size) return null;
  const batches = [];
  const taken = [];
  for (const [material, parts] of groups) {
    for (const p of parts) taken.push(p.k);
    const paint = parts[0].paint;
    const { geo, ranges } = mergeParts(parts, !paint && !!material.map);
    if (paint) {
      const first = paintInfo.get(parts[0].geo);
      const all = [];
      parts.forEach((p, k) => {
        for (const r of paintInfo.get(p.geo).ranges) all.push({ start: r.start + ranges[k].start, count: r.count, pal: r.pal });
      });
      paintInfo.set(geo, { ranges: all, palette: first.palette, trim: first.trim, camo: first.camo, variants: new Map() });
    }
    batches.push({ geo, material, paint });
  }
  return { batches, taken };
}

function buildTemplate(stem, gltf, opts = {}) {
  const trim = opts.trim != null ? new THREE.Color(opts.trim).getHex() : null;
  const assoc = gltf.parser.associations;
  const named = (o) => {
    const a = assoc.get(o);
    return !!a && a.nodes !== undefined && !o.name.startsWith(HOLDER);
  };
  const scene = gltf.scene;
  scene.updateMatrixWorld(true);

  const palette = [];
  const palIndex = new Map(); // source material → palette index
  const special = new Map(); // source material → converted material
  const palOf = (m) => {
    let i = palIndex.get(m);
    if (i === undefined) {
      i = palette.length;
      palette.push(paletteEntry(m));
      palIndex.set(m, i);
    }
    return i;
  };

  const inv = new THREE.Matrix4();
  const clone = (src) => {
    const out = new THREE.Group();
    out.name = src.name;
    out.userData.part = true;
    out.position.copy(src.position);
    out.quaternion.copy(src.quaternion);
    out.scale.copy(src.scale);
    // This node's own primitives.
    const paint = new Map(); // side → parts
    const spec = new Map(); // material → parts
    inv.copy(src.matrixWorld).invert();
    const collect = (o) => {
      for (const c of o.children) {
        if (named(c)) continue;
        if (c.isMesh) {
          const mats = Array.isArray(c.material) ? c.material : [c.material];
          const m = mats[0];
          const mat = _m4.multiplyMatrices(inv, c.matrixWorld).clone();
          if (isPaint(m)) {
            const key = m.side === THREE.DoubleSide ? "ds" : "fs";
            if (!paint.has(key)) paint.set(key, []);
            paint.get(key).push({ geo: c.geometry, mat, pal: palOf(m) });
          } else {
            if (!spec.has(m)) spec.set(m, []);
            spec.get(m).push({ geo: c.geometry, mat, pal: -1 });
          }
        }
        collect(c);
      }
    };
    collect(src);
    for (const [side, parts] of paint) {
      const { geo, ranges } = mergeParts(parts, false);
      paintInfo.set(geo, { ranges, palette, trim, camo: opts.camo ?? null, variants: new Map() });
      const decal = /_Livery_/.test(src.name);
      const mesh = new THREE.Mesh(geo, decal ? paintMaterialDecal : side === "ds" ? paintMaterialDS : paintMaterial);
      mesh.name = `${src.name}#paint`;
      mesh.userData.paint = true;
      out.add(mesh);
    }
    for (const [m, parts] of spec) {
      const { geo } = mergeParts(parts, !!m.map);
      let conv = special.get(m);
      if (!conv) {
        conv = lambertOf(m);
        special.set(m, conv);
      }
      const mesh = new THREE.Mesh(geo, conv);
      mesh.name = `${src.name}#${m.name}`;
      if (conv.transparent) mesh.renderOrder = 1;
      // A team-hued special (a parachute's silk panels) repaints per team —
      // or, in a teamless family, wears the trim once and for all.
      if (/Team$/.test(m.name)) {
        if (trim == null) mesh.userData.teamSpecial = true;
        else mesh.material = trimSpecial(conv, trim);
      }
      out.add(mesh);
    }
    for (const c of src.children) if (named(c)) out.add(clone(c));
    return out;
  };

  // The glTF scene holds the model root node (`Spitfire`, `EssexReal`…).
  const root = new THREE.Group();
  root.name = stem;
  for (const c of scene.children) if (named(c)) root.add(clone(c));
  finishPalette(palette);
  // Default paint (team 0, factory) so a template is drawable on its own.
  root.traverse((o) => {
    if (o.userData.paint) o.geometry = painted(o.geometry, 0, null);
  });

  // The node prefix: the text up to the first underscore that every part name
  // shares (`SP_`, `B17_`). Callers may override it with the manifest's value.
  const names = [];
  root.traverse((o) => {
    if (o.userData.part && o.parent !== root) names.push(o.name);
  });
  let prefix = "";
  if (names.length) {
    const first = names[0];
    const cut = first.indexOf("_");
    const cand = cut > 0 ? first.slice(0, cut + 1) : "";
    if (cand && names.every((n) => n.startsWith(cand))) prefix = cand;
  }
  const t = new Template(stem, root, palette, prefix, trim);
  t.livery = opts.livery ?? null;
  return t;
}

// A scheme's identity for the variant caches: a livery by its id (its paint is
// a whole table), a plain scheme by the two colors it changes.
function schemeKey(scheme) {
  if (!scheme) return "";
  return scheme.key ?? `${scheme.body ?? ""}/${scheme.accent ?? ""}`;
}

// The livery decal set a scheme shows: its own, or — for the factory finish —
// the family's; a plain recolor (Midnight Reaper, Duneworn…) repaints the
// mottle away with the rest of the skin and shows none.
export function liveryOf(scheme, factory = null) {
  if (!scheme) return factory;
  if (scheme.livery) return scheme.livery;
  return scheme.body || scheme.accent || scheme.paint ? null : factory;
}

// The camouflage a level without decals blends into its uppers and flanks:
// the factory finish's (`camo`), or a livery's own `Camo`. A plain recolor
// paints the pattern out, and a livery that names no Camo lays none.
const _camo = new THREE.Color();
function camoOf(scheme, factory) {
  if (factory == null) return null;
  if (!scheme) return factory;
  return scheme.paint?.Camo ?? null;
}

// The painted variant of a base paint geometry for (team, scheme). Position,
// normal and index buffers are SHARED with the base — only the colors differ.
// A teamless family keys its variants by scheme alone, so every team gets the
// very same geometry.
function painted(geo, team, scheme) {
  const base = geo.userData.paintBase ?? geo;
  const info = paintInfo.get(base);
  if (!info) return geo;
  const key = `${info.trim == null ? team : "-"}|${schemeKey(scheme)}`;
  let v = info.variants.get(key);
  if (v) return v;
  const count = base.attributes.position.count;
  const col = new Float32Array(count * 3);
  const camo = camoOf(scheme, info.camo);
  for (const r of info.ranges) {
    const e = info.palette[r.pal];
    paintColor(e, team, scheme, _c, info.trim);
    if (camo && (e.group === "Upper" || e.group === "Side")) {
      _camo.set(camo);
      if (e.ctl) _camo.multiplyScalar(e.ctlK);
      _c.lerp(_camo, 0.5);
    }
    for (let i = r.start; i < r.start + r.count; i++) {
      col[i * 3] = _c.r;
      col[i * 3 + 1] = _c.g;
      col[i * 3 + 2] = _c.b;
    }
  }
  v = new THREE.BufferGeometry();
  v.setIndex(base.index);
  v.setAttribute("position", base.attributes.position);
  v.setAttribute("normal", base.attributes.normal);
  v.setAttribute("color", new THREE.BufferAttribute(col, 3));
  v.boundingBox = base.boundingBox;
  v.boundingSphere = base.boundingSphere;
  v.userData.paintBase = base;
  info.variants.set(key, v);
  return v;
}

// Team-hued special materials (parachute silk), one clone per team.
const teamSpecials = new Map();
function teamSpecial(mat, team) {
  const key = `${mat.uuid}|${team}`;
  let m = teamSpecials.get(key);
  if (!m) {
    m = mat.clone();
    m.color.set(TEAM_INFO[team]?.color ?? TEAM_INFO[0].color);
    teamSpecials.set(key, m);
  }
  return m;
}
// The same special in a teamless family's trim, one clone per material.
function trimSpecial(mat, trim) {
  const key = `${mat.uuid}|trim`;
  let m = teamSpecials.get(key);
  if (!m) {
    m = mat.clone();
    m.color.set(trim);
    teamSpecials.set(key, m);
  }
  return m;
}

// --- public API ---------------------------------------------------------------------

const templates = new Map(); // stem → Promise<Template>
const ready = new Map(); // stem → Template (resolved)
const kits = new Map(); // stem → Promise<json>
let pending = 0; // loads in flight (the screenshot director waits for zero)

/** How many model/kit loads are still in flight. */
export function modelsPending() {
  return pending;
}
const track = (p) => {
  pending++;
  const done = () => {
    pending--;
  };
  p.then(done, done);
  return p;
};

// Per-stem build options, registered by the unit catalogs before they load
// anything (the aircraft register their factory trim here).
const buildOpts = new Map();
/** Options for a model family: { trim: hex } paints it teamless (`*_Team` wears the trim);
 * { livery: name } is the `<prefix>Livery_<name>` decal set its factory finish shows;
 * { camo: hex } is the camouflage a level without those decals mixes into `Upper`/`Side`. */
export function setModelOptions(stem, opts) {
  buildOpts.set(stem, opts);
}

// A re-cut airframe (`source` + `clipSpan`, the Spitfire Mk XVI's clipped
// wing): its levels are another model's files, and everything that reaches
// past `span` either side of the centreline is folded flat onto that station.
// Cut at a station of the art's own, the skin beyond it -- the bay and the
// elliptical tip -- lands inside the station's section and closes it, a
// squared cap with the tip's own paint; the cap's faces take its flat normal,
// so it lights as one plate. A decal (a livery's or a scorch's) has no cap
// to make: what lies wholly past the cut goes, and what straddles it is
// squeezed to stop at the edge.
const DECAL_NODE = /Livery_|Scorch|Stain/;
function clipSpan(scene, span) {
  scene.updateMatrixWorld(true);
  const v = new THREE.Vector3(), inv = new THREE.Matrix4(), nm = new THREE.Matrix3();
  scene.traverse((o) => {
    if (!o.isMesh) return;
    let decal = false;
    for (let n = o; n && !decal; n = n.parent) decal = DECAL_NODE.test(n.name);
    const src = o.geometry, P = src.attributes.position;
    let far = false;
    for (let i = 0; i < P.count && !far; i++) far = Math.abs(v.fromBufferAttribute(P, i).applyMatrix4(o.matrixWorld).z) > span;
    if (!far) return;
    const g = src.index ? src.toNonIndexed() : src.clone();
    const pos = g.attributes.position, nor = g.attributes.normal;
    inv.copy(o.matrixWorld).invert();
    nm.getNormalMatrix(inv);
    const keep = [];
    const w = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    for (let t = 0; t < pos.count; t += 3) {
      let out = 0;
      for (let k = 0; k < 3; k++) {
        w[k].fromBufferAttribute(pos, t + k).applyMatrix4(o.matrixWorld);
        if (Math.abs(w[k].z) > span) {
          out++;
          w[k].z = Math.sign(w[k].z) * span;
        }
      }
      if (decal && out === 3) continue;
      const cap = !decal && w.every((p) => Math.abs(Math.abs(p.z) - span) < 1e-4);
      if (cap) {
        // The tip's upper skin folds onto the cap wound one way, its lower
        // skin the other: wind every cap face to look outboard, as drawn.
        const side = Math.sign(w[0].z);
        const nz = (w[1].x - w[0].x) * (w[2].y - w[0].y) - (w[1].y - w[0].y) * (w[2].x - w[0].x);
        if (nz * side < 0) {
          [w[1], w[2]] = [w[2], w[1]];
          for (const a of Object.values(g.attributes)) {
            for (let c = 0; c < a.itemSize; c++) {
              const t1 = a.array[(t + 1) * a.itemSize + c];
              a.array[(t + 1) * a.itemSize + c] = a.array[(t + 2) * a.itemSize + c];
              a.array[(t + 2) * a.itemSize + c] = t1;
            }
          }
        }
      }
      for (let k = 0; k < 3; k++) {
        v.copy(w[k]).applyMatrix4(inv);
        pos.setXYZ(t + k, v.x, v.y, v.z);
        if (cap && nor) {
          v.set(0, 0, Math.sign(w[k].z)).applyMatrix3(nm).normalize();
          nor.setXYZ(t + k, v.x, v.y, v.z);
        }
      }
      keep.push(t);
    }
    if (keep.length * 3 < pos.count) {
      // drop the decal faces past the cut: compact every attribute
      for (const [name, a] of Object.entries(g.attributes)) {
        const s = a.itemSize, arr = new a.array.constructor(keep.length * 3 * s);
        keep.forEach((t, j) => arr.set(a.array.subarray(t * s, (t + 3) * s), j * 3 * s));
        g.setAttribute(name, new THREE.BufferAttribute(arr, s, a.normalized));
      }
    }
    g.computeBoundingBox();
    g.computeBoundingSphere();
    o.geometry = g;
  });
}

/** Load (once) and merge a model. Resolves to its Template; rejects on failure. */
export function loadModel(stem) {
  let p = templates.get(stem);
  if (!p) {
    const opts = buildOpts.get(stem);
    p = fetchGlb(opts?.source ?? stem)
      .then((buf) => loader.parseAsync(buf, ""))
      .then((gltf) => {
        if (opts?.clipSpan) clipSpan(gltf.scene, opts.clipSpan);
        const t = buildTemplate(stem, gltf, opts);
        ready.set(stem, t);
        return t;
      });
    // A failed load stays failed for the session (the boxes carry on) but
    // must not surface as an unhandled rejection for every caller.
    p.catch((err) => console.warn(`models: ${stem} unavailable —`, err?.message ?? err));
    templates.set(stem, track(p));
  }
  return p;
}

/** The Template if it has already loaded, else null (never starts a load). */
export function modelIfReady(stem) {
  return ready.get(stem) ?? null;
}

/** The damage kit description (`<stem>_damage.json`), loaded once. */
export function loadDamageKit(stem) {
  let p = kits.get(stem);
  if (!p) {
    // A re-cut airframe flies its base's kit, cut where its wing is: the torn
    // tip ends at the cut, and no bullet hole lands on skin that isn't there.
    const o = buildOpts.get(`${stem}_damage`);
    p = fetchJson(o?.source?.replace(/_damage$/, "") ?? stem);
    if (o?.clipSpan) {
      const span = o.clipSpan;
      p = p.then((kit) => {
        const k = structuredClone(kit);
        for (const w of Object.values(k.wing ?? {})) if (w.tipSpan > span) w.tipSpan = span;
        for (const [c, s] of Object.entries(k.samples ?? {})) k.samples[c] = s.filter((q) => Math.abs(q[2]) <= span);
        return k;
      });
    }
    p.catch(() => {});
    kits.set(stem, track(p));
  }
  return p;
}

/**
 * One drawable copy of a model, painted for a team (and optional scheme
 * { body, accent }). Geometry is shared with every other instance; nodes are
 * this instance's own, so they can spin, yaw, pitch and hide independently.
 */
export class ModelInstance {
  constructor(template, { team = 0, scheme = null } = {}) {
    this.template = template;
    this.root = template.root.clone(true);
    this._nodes = new Map();
    const shade = GFX >= 2; // tier 2's sun shadow: every model casts and takes it
    this.root.traverse((o) => {
      if (o.userData.part) this._nodes.set(o.name, o);
      if (shade && o.isMesh) o.castShadow = o.receiveShadow = true;
    });
    // The family's livery decal sets, by name (`BF_Livery_Galland` → Galland).
    const lre = new RegExp(`^${template.prefix}Livery_([A-Za-z]+)$`);
    this._liveries = [];
    for (const [name, node] of this._nodes) {
      const m = lre.exec(name);
      if (m) this._liveries.push([m[1], node]);
    }
    this.team = -1;
    this.scheme = undefined;
    this.paint(team, scheme);
  }

  /** Repaint for a team and scheme (cheap: swaps shared color variants). */
  paint(team, scheme = null) {
    const sKey = schemeKey(scheme);
    if (team === this.team && sKey === this.scheme) return;
    this.team = team;
    this.scheme = sKey;
    this._schemeDef = scheme;
    this.root.traverse((o) => {
      if (o.userData.paint) o.geometry = painted(o.geometry, team, scheme);
      else if (o.userData.teamSpecial) {
        o.userData.srcMat ??= o.material;
        o.material = teamSpecial(o.userData.srcMat, team);
      }
    });
    const want = liveryOf(scheme, this.template.livery);
    for (const [name, node] of this._liveries) node.visible = name === want;
  }

  /** A node by full or prefix-less name, or null. It's the caller's to
   * move, so a settle() above it never freezes it (see WorldScene). */
  node(name) {
    const o = this._nodes.get(name) ?? this._nodes.get(this.template.prefix + name) ?? null;
    if (o) holdLive(o);
    return o;
  }

  /**
   * Draw this instance's rigid parts as one mesh per material: the far
   * airframe's batch (flightsim-2ib). A model is a tree of named parts, each
   * its own draw, so a Spitfire at a kilometre cost eight draws for a shape
   * eight pixels long, and a B-17 twenty-two. Here every mesh not under a
   * `keep` node (a spinning prop, a turret, a livery that comes and goes)
   * joins one merged mesh per material on the root, and the originals hide.
   *
   * The originals stay in the tree, so nothing that reaches for a part breaks:
   * setFlat(false) puts them back the moment something has to show or hide
   * one on its own (a wing shot off). Paint still works — the merged paint
   * mesh is a paint base of its own, recoloured by paint() like any part.
   * The merge is cached on the template, so a squadron shares one geometry.
   * Returns false (and changes nothing) when there's nothing worth merging.
   */
  flatten(keep = []) {
    if (this._flat !== undefined) return !!this._flat;
    const t = this.template;
    const kept = new Set(keep);
    for (const [, node] of this._liveries) kept.add(node);
    const root = this.root;
    root.updateMatrixWorld(true);
    const parts = [];
    let i = -1;
    root.traverse((o) => {
      if (!o.isMesh) return;
      i++;
      if (o.userData.teamSpecial) return; // repaints by material swap, per team
      for (let p = o; p !== root; p = p.parent) if (!p.visible || kept.has(p)) return;
      parts.push({ o, i });
    });
    const sig = parts.map((p) => p.i).join(",");
    t._flat ??= new Map();
    let built = t._flat.get(sig);
    if (built === undefined) {
      built = flatGeometry(root, parts.map((p) => p.o));
      t._flat.set(sig, built);
    }
    if (!built) {
      this._flat = null;
      return false;
    }
    const shade = GFX >= 2;
    const meshes = built.batches.map(({ geo, material, paint }) => {
      const m = new THREE.Mesh(paint ? painted(geo, this.team, this._schemeDef) : geo, material);
      m.name = `${root.name}#flat`;
      m.userData.paint = paint;
      if (material.transparent) m.renderOrder = 1;
      if (shade) m.castShadow = m.receiveShadow = true;
      root.add(m);
      return m;
    });
    this._flat = { meshes, parts: built.taken.map((k) => parts[k].o), on: false };
    this.setFlat(true);
    return true;
  }

  /** Draw the merged parts (true) or the originals (false); see flatten(). */
  setFlat(on) {
    const f = this._flat;
    if (!f || f.on === on) return;
    f.on = on;
    for (const m of f.meshes) m.visible = on;
    for (const o of f.parts) o.visible = !on;
  }

  /** Every node whose part name matches the regex (prefix stripped). */
  nodes(re) {
    const out = [];
    const pre = this.template.prefix;
    for (const [name, o] of this._nodes) {
      const bare = pre && name.startsWith(pre) ? name.slice(pre.length) : name;
      if (re.test(bare)) {
        holdLive(o);
        out.push(o);
      }
    }
    return out;
  }
}

/** Convenience: load a model and resolve to a painted instance. */
export async function instanceOf(stem, opts) {
  const t = await loadModel(stem);
  return new ModelInstance(t, opts);
}

/**
 * How much farther than it is every model level reckons the camera stands:
 * 1, or more while the frame governor (governor.js) is sparing a phone. A
 * squadron at 400 m then picks the level it would pick at 600: the flattened
 * one-batch-a-material airframe (ModelInstance.flatten) instead of a hundred parts,
 * and the matrix walk steps over all of them. three's LOD reads only the
 * camera's position and its zoom, so the bias rides a stand-in camera with
 * the zoom divided down; the real one is never touched.
 */
let lodBias = 1;
const biasCam = { matrixWorld: null, zoom: 1 };
export function setLodBias(k) {
  lodBias = k > 0 ? k : 1;
}

/**
 * The THREE.LOD every airframe, hull, emplacement and building is drawn
 * through: it keeps only the level it draws up to date.
 *
 * A stock LOD holds three complete copies of its model's node tree plus the
 * box silhouette, and three.js recomposes every node's matrix every frame,
 * hidden or not — a moving parent forces the walk, so matrixWorldAutoUpdate
 * can't spare it. On the air map that was ~6,000 matrices a frame nobody
 * drew, a fifth of the main thread (docs/wasm-investigation.md). Here the walk
 * steps over a hidden level instead. The level stays a child (paint, the
 * damage kit and its hole decals, the fin stripe, the cockpit's hidden canopy
 * and dispose all still reach it), and anything that asks a hidden node where
 * it is through getWorldPosition / localToWorld refreshes its own chain as it
 * always did.
 *
 * The catch is ordering: the renderer walks the matrices first and only then
 * asks the LOD which level to show. A level switched on in update() was
 * skipped by this frame's walk, so it's brought up to date right there,
 * before it draws, or it would flash in wherever it last stood.
 */
export class ModelLOD extends THREE.LOD {
  constructor() {
    super();
    this.isModelLOD = true; // WorldScene's walk keys on it, three-style
  }

  updateMatrixWorld(force) {
    if (this.matrixAutoUpdate) this.updateMatrix();
    if (this.matrixWorldNeedsUpdate || force) {
      if (this.parent === null) this.matrixWorld.copy(this.matrix);
      else this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
      this.matrixWorldNeedsUpdate = false;
      force = true;
    }
    const kids = this.children;
    for (let i = 0; i < kids.length; i++) {
      const c = kids[i];
      if (c.visible === false) continue; // nobody draws it this frame
      if (c.matrixWorldAutoUpdate === true || force === true) c.updateMatrixWorld(force);
    }
  }

  update(camera) {
    const levels = this.levels;
    let skipped = 0; // levels the walk stepped over (hidden going in)
    for (let i = 0; i < levels.length; i++) if (!levels[i].object.visible) skipped |= 1 << i;
    if (lodBias !== 1) {
      biasCam.matrixWorld = camera.matrixWorld;
      biasCam.zoom = camera.zoom / lodBias;
      super.update(biasCam);
    } else {
      super.update(camera);
    }
    if (!skipped) return;
    for (let i = 0; i < levels.length; i++) {
      const o = levels[i].object;
      if (o.visible && skipped & (1 << i)) o.updateMatrixWorld(true);
    }
  }
}

/**
 * The game's scene: its matrix walk does only the work something asked for.
 *
 * three.js's stock walk can't tell a node that moved from one that didn't.
 * Every node recomposes its matrix from its position, quaternion and scale
 * each frame, and doing so flags its world matrix stale, so the scene root
 * itself — recomposing its own identity — forces every node in the world to
 * be re-multiplied too. On the air map that's ~3,000 composes and ~4,000
 * multiplies a frame for a world that's mostly standing still: hangars,
 * flak plinths, parked Spitfires, the ground itself, and inside every flying
 * plane the hundred-odd parts that ride along without moving on their own.
 *
 * Here a node is composed only when its position, quaternion or scale reads
 * differently from the last time it was composed, and re-multiplied only
 * when it was recomposed, its parent's world matrix changed, it was given a
 * new parent, or someone flagged matrixWorldNeedsUpdate. A turret that
 * traverses is composed and carries its gun along; the plinth under it is
 * left alone. Nothing has to be told it moved: anything that moves a node by
 * its position, rotation or scale — every animator in the game — is seen by
 * the comparison.
 *
 * That still visits every node, and a visit is most of what's left: a cold
 * pointer chase per node, ~4,000 a frame. So a placement that knows its prop
 * never moves can settle() it (below), and the walk then leaves the whole
 * subtree alone until its parent moves, it changes parent, it's flagged
 * (updateMatrix()), or something is added beneath it.
 *
 * The one thing it can't see is a world matrix edited in place, which the
 * stock walk would silently have rebuilt next frame. That's the muzzle
 * flash's flicker (flipbooks.js), and it flags itself stale for the next
 * frame. A node class with its own updateMatrixWorld (a camera in the
 * scene, a skinned mesh) is handed its own walk, forced when its parent
 * moved; a ModelLOD's hidden levels are stepped over as ModelLOD does.
 */
export class WorldScene extends THREE.Scene {
  updateMatrixWorld(force) {
    recompose(this);
    if (this.matrixWorldNeedsUpdate || force) {
      this.matrixWorld.copy(this.matrix);
      this.matrixWorldNeedsUpdate = false;
      force = true;
    }
    this._walkDirty = false;
    walkKids(this, force === true);
  }
}

/**
 * Settle a prop that will never move against its parent: a hangar, a parked
 * reserve plane, a chunk of ground. WorldScene stops walking it (three's
 * matrixWorldAutoUpdate off) and wakes it only when it must: its parent
 * moved (a deck mount on a steaming hull is walked every frame, as before),
 * it changed parent, it was flagged with updateMatrix(), or something was
 * added under it (a model landing in its box stand-in's slot).
 *
 * Whatever an animator was handed by ModelInstance.node()/nodes() stays live,
 * whichever came first: settle() leaves the path to such a node walked and
 * settles only its rigid neighbours, and a node handed out later wakes its
 * own path. Anything else that moves inside a settled prop has to say so
 * (updateMatrix()), so settle only what nothing animates by hand.
 */
export function settle(o) {
  if (o._walkLive === true) {
    for (const c of o.children) settle(c);
  } else {
    o.matrixWorldAutoUpdate = false;
  }
}

// A node an animator holds: it and every node above it stay walked, now and
// whenever its subtree is added somewhere new.
function holdLive(o) {
  holdPath(o, o.parent);
}

// Mark the path from `low` up through `p` and its ancestors as leading to a
// held node. If any of it was settled, the settle moves down off the path:
// everything beside it, from the highest settled node to the held subtree's
// own parts, is still rigid; only the path itself must be walked.
function holdPath(low, p) {
  const path = [low];
  let top = low.matrixWorldAutoUpdate === false ? 0 : -1;
  low._walkLive = true;
  for (; p !== null; p = p.parent) {
    p._walkLive = true;
    path.push(p);
    if (p.matrixWorldAutoUpdate === false) top = path.length - 1;
  }
  for (let i = top; i >= 0; i--) {
    path[i].matrixWorldAutoUpdate = true;
    for (const c of path[i].children) if (c !== path[i - 1]) settle(c);
  }
}

// Something added under a settled prop must be placed: flag the path down to
// it for the next walk (and keep a held node's new ancestors walked).
const stockAdd = THREE.Object3D.prototype.add;
THREE.Object3D.prototype.add = function add(...objects) {
  stockAdd.apply(this, objects);
  for (let p = this; p !== null; p = p.parent) p._walkDirty = true;
  for (const o of objects) if (o?._walkLive === true && o.parent === this) holdPath(o, this);
  return this;
};

const STOCK_WALK = THREE.Object3D.prototype.updateMatrixWorld;

// Compose o's matrix if its position, quaternion or scale moved since the
// last compose (flagging its world matrix stale, as updateMatrix does). The
// last-composed values live in a plain array: V8 keeps a small one inline
// on the heap beside its node, where a Float64Array's backing store isn't.
function recompose(o) {
  if (!o.matrixAutoUpdate) return;
  const p = o.position, q = o.quaternion, s = o.scale;
  const c = o._walkTRS;
  if (
    c !== undefined &&
    c[0] === p.x && c[1] === p.y && c[2] === p.z &&
    c[3] === q._x && c[4] === q._y && c[5] === q._z && c[6] === q._w &&
    c[7] === s.x && c[8] === s.y && c[9] === s.z
  ) return;
  if (c === undefined) {
    o._walkTRS = [p.x, p.y, p.z, q._x, q._y, q._z, q._w, s.x, s.y, s.z];
  } else {
    c[0] = p.x; c[1] = p.y; c[2] = p.z;
    c[3] = q._x; c[4] = q._y; c[5] = q._z; c[6] = q._w;
    c[7] = s.x; c[8] = s.y; c[9] = s.z;
  }
  o.matrix.compose(p, q, s);
  o.matrixWorldNeedsUpdate = true;
}

// `moved`: o's world matrix was just rebuilt, so every child's must be.
function walkKids(o, moved) {
  const kids = o.children;
  const lod = o.isModelLOD === true;
  for (let i = 0; i < kids.length; i++) {
    const c = kids[i];
    if (lod && c.visible === false) continue; // nobody draws it this frame (see ModelLOD)
    if (c.matrixWorldAutoUpdate === true || moved || c._walkDirty === true || c.matrixWorldNeedsUpdate || c._walkParent !== o) {
      walk(c, moved);
    }
  }
}

function walk(o, moved) {
  if (o._walkParent !== o.parent) {
    o._walkParent = o.parent; // new to this parent: its world matrix is a stranger's
    moved = true;
  }
  o._walkDirty = false;
  if (o.updateMatrixWorld !== STOCK_WALK && o.isModelLOD !== true) {
    o.updateMatrixWorld(moved);
    return;
  }
  recompose(o);
  if (o.matrixWorldNeedsUpdate || moved) {
    o.matrixWorld.multiplyMatrices(o.parent.matrixWorld, o.matrix);
    o.matrixWorldNeedsUpdate = false;
    moved = true;
  }
  walkKids(o, moved);
}

/**
 * Replace a THREE.LOD's levels in one go: [[object, distance], ...]. Objects
 * already in the LOD but not in the list are removed; nothing is disposed
 * (geometry belongs to the shared templates).
 */
export function setLodLevels(lod, levels, hysteresis = 0.08) {
  for (const l of lod.levels) lod.remove(l.object);
  lod.levels.length = 0;
  for (const [obj, dist] of levels) lod.addLevel(obj, dist, hysteresis);
}

/**
 * A small model flattened to ONE standalone geometry — every paint mesh, in
 * model space, painted for `team`, optionally transformed by `matrix` — for
 * instancing (ordnance, parachutes, ground crew: hundreds of copies, one draw
 * call). Special meshes (glass, overlays) are left out; the flat models these
 * are used for don't have any. Returns { geometry, doubleSided }.
 */
export function bakedGeometry(template, { team = 0, scheme = null, matrix = null } = {}) {
  const inst = new ModelInstance(template, { team, scheme });
  inst.root.updateMatrixWorld(true);
  const parts = [];
  let doubleSided = false;
  inst.root.traverse((o) => {
    if (!o.isMesh || !o.userData.paint) return;
    // a livery the scheme doesn't show stays out of the bake
    for (let p = o; p; p = p.parent) if (!p.visible) return;
    const g = o.geometry.clone();
    g.applyMatrix4(o.matrixWorld);
    if (matrix) g.applyMatrix4(matrix);
    parts.push(g);
    if (o.material.side === THREE.DoubleSide) doubleSided = true;
  });
  if (parts.length === 1) return { geometry: parts[0], doubleSided };
  // Several parts: concatenate (same attribute set: position, normal, color).
  let verts = 0, idx = 0;
  for (const g of parts) {
    verts += g.attributes.position.count;
    idx += g.index.count;
  }
  const pos = new Float32Array(verts * 3), nor = new Float32Array(verts * 3), col = new Float32Array(verts * 3);
  const index = new Uint32Array(idx);
  let vo = 0, io = 0;
  for (const g of parts) {
    pos.set(g.attributes.position.array, vo * 3);
    nor.set(g.attributes.normal.array, vo * 3);
    col.set(g.attributes.color.array, vo * 3);
    for (let i = 0; i < g.index.count; i++) index[io + i] = g.index.getX(i) + vo;
    vo += g.attributes.position.count;
    io += g.index.count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  out.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  out.setAttribute("color", new THREE.BufferAttribute(col, 3));
  out.setIndex(new THREE.BufferAttribute(index, 1));
  out.computeBoundingSphere();
  return { geometry: out, doubleSided };
}
